// Segmented day-window selector (D-IR-5), accent-pill styling. The single
// shared look for every "pick a report/chart window" control in the app —
// reuse this (with a custom `options`/`formatLabel` if the day set or label
// text differs) rather than hand-rolling another button row.

export type RangeDays = 7 | 30 | 60 | 90;

const OPTIONS: RangeDays[] = [7, 30, 60, 90];

interface Props<T extends number> {
  value: T;
  onChange: (value: T) => void;
  /** Day values to offer. Defaults to the standard 7/30/60/90 set. */
  options?: readonly T[];
  /** Button label per value. Defaults to "{n}d". */
  formatLabel?: (n: T) => string;
}

export function ReportRangeSelector<T extends number = RangeDays>({
  value,
  onChange,
  options = OPTIONS as unknown as readonly T[],
  formatLabel = (n: T) => `${n}d`,
}: Props<T>) {
  return (
    <div className="flex flex-wrap rounded-full bg-gray-100 p-0.5 text-xs font-medium dark:bg-gray-800">
      {options.map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          className={[
            "min-h-[44px] rounded-full px-3 transition-all sm:min-h-0 sm:py-1",
            value === n
              ? "bg-[color:var(--accent,#2563eb)] text-white shadow-sm"
              : "text-gray-500 hover:text-gray-700 dark:text-gray-300",
          ].join(" ")}
          aria-pressed={value === n}
        >
          {formatLabel(n)}
        </button>
      ))}
    </div>
  );
}
